/**
 * ResourcePool — Vista di allocazione lavoratori × cantieri × giorni.
 *
 * Modalità:
 *   compatta   → cella colorata (fino a 3 cantieri divisi)
 *   dettagliata → mostra ore/attività + nome breve
 *
 * Drag & Drop:
 *   Trascina una cella verso un'altra riga (stesso giorno) per
 *   riassegnare l'attività a un altro lavoratore. Il backend
 *   /api/resource-pool/reassign verifica ferie + sovrapposizioni.
 */
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  useDraggable,
  useDroppable,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { toast } from "sonner";
import api from "@/api";
import { addDays, isoDate } from "@/lib/ganttScale";
import { useAuth, isAdmin } from "@/context/AuthContext";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import { Users, ChevronLeft, ChevronRight, LayoutGrid, List } from "lucide-react";

const FERIE_COLOR = "#f472b6";

function fmtDay(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return {
    day: String(d.getUTCDate()).padStart(2, "0"),
    month: String(d.getUTCMonth() + 1).padStart(2, "0"),
    weekday: d.getUTCDay(),
  };
}

function DraggableCell({ id, disabled, children, className, testid, title, onClick }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id, disabled });
  return (
    <div
      ref={setNodeRef}
      {...(disabled ? {} : { ...listeners, ...attributes })}
      onClick={onClick}
      className={`${className} ${isDragging ? "opacity-40" : ""}`}
      data-testid={testid}
      title={title}
    >
      {children}
    </div>
  );
}

function DroppableCell({ id, children, isOver }) {
  const { setNodeRef } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`w-full h-full ${isOver ? "ring-2 ring-inset ring-blue-400" : ""}`}
    >
      {children}
    </div>
  );
}

export default function ResourcePool() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [center, setCenter] = useState(new Date());
  const [days, setDays] = useState(30);
  const [mode, setMode] = useState("compatta"); // compatta | dettagliata

  const start = isoDate(addDays(center, -Math.floor(days / 2)));
  const end = isoDate(addDays(center, Math.ceil(days / 2) - 1));

  const { data, isLoading } = useQuery({
    queryKey: ["resource-pool", start, end],
    queryFn: () =>
      api.get(`/resource-pool`, { params: { data_inizio: start, data_fine: end } }).then((r) => r.data),
    enabled: isAdmin(user),
    staleTime: 30_000,
  });

  const grid = useMemo(() => buildGrid(data), [data]);

  // Realtime: se un'altra sessione modifica un'attività ricarica
  useLiveUpdates({
    rooms: ["global"],
    onEvent: (msg) => {
      if (msg?.type?.startsWith("attivita.")) {
        qc.invalidateQueries({ queryKey: ["resource-pool"] });
      }
    },
  });

  const reassign = useMutation({
    mutationFn: (payload) => api.post("/resource-pool/reassign", payload).then((r) => r.data),
    onSuccess: (res) => {
      if (res?.ok) {
        toast.success("Assegnazione aggiornata");
        qc.invalidateQueries({ queryKey: ["resource-pool"] });
      } else if (res?.conflicts?.length) {
        const conf = res.conflicts;
        const summary = conf
          .map((c) =>
            c.tipo === "ferie"
              ? `Ferie ${c.data_inizio} → ${c.data_fine}`
              : `Attività "${c.nome}" (${c.data_inizio} → ${c.data_fine})`
          )
          .join("\n");
        if (window.confirm(`Conflitti rilevati:\n\n${summary}\n\nAssegnare comunque?`)) {
          reassign.mutate({ ...res._pendingPayload, force: true });
        }
      }
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore riassegnazione"),
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const handleDragEnd = (evt) => {
    if (!evt.over || !evt.active) return;
    const source = parseCellId(evt.active.id);
    const target = parseCellId(evt.over.id);
    if (!source || !target) return;
    if (source.lavId === target.lavId) return;
    if (source.giorno !== target.giorno) {
      toast.error("Puoi trascinare solo nello stesso giorno (per ora)");
      return;
    }
    if (!source.attivitaId) {
      toast.error("Assegnazione senza attività Gantt — modifica direttamente il cantiere");
      return;
    }
    const payload = {
      attivita_id: source.attivitaId,
      from_lavoratore_id: source.lavId,
      to_lavoratore_id: target.lavId,
    };
    // guarda se il target è in ferie quel giorno → warning immediato
    const targetInFerie = grid.ferieMap.get(`${target.lavId}|${target.giorno}`);
    if (targetInFerie) {
      if (!window.confirm(`${targetInFerie.toUpperCase()} approvata per il target il ${target.giorno}. Assegnare comunque?`)) {
        return;
      }
      payload.force = true;
    }
    // memorizza per eventuale re-invio con force
    reassign.mutate(payload, {
      onSuccess: (res) => {
        if (!res?.ok) res._pendingPayload = payload;
      },
    });
  };

  if (!isAdmin(user)) {
    return (
      <div className="p-6 text-sm text-slate-600">
        Vista riservata agli amministratori.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Gantt</div>
          <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
            <Users className="w-7 h-7 text-slate-500" /> Resource Pool
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Trascina una cella su un'altra riga (stesso giorno) per riassegnare l'attività.
            Ferie/permessi approvati sono evidenziati con hatch pattern e mostrano un avviso al drop.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="rp-mode">
            <button
              onClick={() => setMode("compatta")}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1 ${mode === "compatta" ? "bg-slate-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="rp-mode-compatta"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Compatta
            </button>
            <button
              onClick={() => setMode("dettagliata")}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1 ${mode === "dettagliata" ? "bg-slate-800 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="rp-mode-dettagliata"
            >
              <List className="w-3.5 h-3.5" /> Dettagliata
            </button>
          </div>
          <button
            onClick={() => setCenter(addDays(center, -days))}
            className="p-2 border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="rp-prev"
            title="Periodo precedente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="px-3 py-2 text-xs font-mono bg-white border border-slate-300 rounded-sm">
            {start} → {end}
          </div>
          <button
            onClick={() => setCenter(addDays(center, days))}
            className="p-2 border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="rp-next"
            title="Periodo successivo"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            data-testid="rp-days"
            className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white"
          >
            <option value={14}>14 gg</option>
            <option value={30}>30 gg</option>
            <option value={60}>60 gg</option>
            <option value={90}>90 gg</option>
          </select>
          <button
            onClick={() => setCenter(new Date())}
            className="px-3 py-2 text-xs font-semibold border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="rp-today"
          >
            Oggi
          </button>
        </div>
      </div>

      {data?.cantieri?.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="rp-legend">
          {data.cantieri.map((c) => (
            <div key={c.cantiere_id} className="inline-flex items-center gap-1.5 px-2 py-1 border border-slate-200 rounded-sm bg-white text-xs">
              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: c.colore }} />
              {c.nome}
            </div>
          ))}
          <div className="inline-flex items-center gap-1.5 px-2 py-1 border border-slate-200 rounded-sm bg-white text-xs">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{
                background: FERIE_COLOR,
                backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,.4) 0 3px, transparent 3px 6px)",
              }}
            />
            Ferie/permessi
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="p-8 text-center text-sm text-slate-400">Caricamento…</div>
      ) : !data?.lavoratori?.length ? (
        <div className="p-8 text-center text-sm text-slate-400 bg-white border border-slate-200 rounded-sm">
          Nessun lavoratore attivo. Crea prima i lavoratori in <b>Lavoratori</b>.
        </div>
      ) : (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="rp-grid">
            <table className="w-full text-xs" style={{ minWidth: 800 }}>
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 border-r border-slate-200 sticky left-0 bg-slate-50 z-20" style={{ minWidth: 180 }}>
                    Lavoratore
                  </th>
                  {grid.giorni.map((iso) => {
                    const info = fmtDay(iso);
                    const weekend = info.weekday === 0 || info.weekday === 6;
                    return (
                      <th
                        key={iso}
                        className={`text-center p-1 border-r border-slate-100 ${weekend ? "bg-slate-100 text-slate-500" : "text-slate-700"}`}
                        style={{ minWidth: mode === "dettagliata" ? 96 : 26 }}
                        title={iso}
                      >
                        <div className="font-mono font-bold">{info.day}</div>
                        <div className="font-mono text-[9px] opacity-60">{info.month}</div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {data.lavoratori.map((l) => (
                  <tr key={l.lavoratore_id} className="border-t border-slate-100">
                    <td className="p-2 border-r border-slate-200 sticky left-0 bg-white z-10">
                      <div className="font-semibold text-slate-800 truncate">
                        {l.nome} {l.cognome}
                      </div>
                      {l.mansione && <div className="text-[10px] text-slate-500 truncate">{l.mansione}</div>}
                    </td>
                    {grid.giorni.map((iso) => {
                      const cellsList = grid.byLavGiorno.get(`${l.lavoratore_id}|${iso}`) || [];
                      const ferie = grid.ferieMap.get(`${l.lavoratore_id}|${iso}`);
                      const info = fmtDay(iso);
                      const weekend = info.weekday === 0 || info.weekday === 6;
                      const cellId = `cell|${l.lavoratore_id}|${iso}`;
                      return (
                        <td
                          key={iso}
                          className={`p-0 border-r border-slate-100 ${weekend ? "bg-slate-50/60" : ""}`}
                          data-testid={`rp-cell-${l.lavoratore_id}-${iso}`}
                          title={cellTitle(cellsList, ferie, iso)}
                          style={{ height: mode === "dettagliata" ? 44 : 26 }}
                        >
                          <DroppableCell id={cellId}>
                            {ferie ? (
                              <div
                                className="w-full h-full"
                                style={{
                                  background: FERIE_COLOR,
                                  backgroundImage:
                                    "repeating-linear-gradient(45deg, rgba(255,255,255,.4) 0 3px, transparent 3px 6px)",
                                }}
                              />
                            ) : cellsList.length === 0 ? null : mode === "compatta" ? (
                              <div className="w-full h-full flex">
                                {cellsList.slice(0, 3).map((cc, i) => (
                                  <DraggableCell
                                    key={i}
                                    id={`drag|${l.lavoratore_id}|${iso}|${cc.attivita_id || ""}|${cc.cantiere_id}`}
                                    disabled={!cc.attivita_id}
                                    className="flex-1 h-full"
                                    testid={`rp-drag-${l.lavoratore_id}-${iso}-${i}`}
                                    title={cellTitle([cc], null, iso)}
                                  >
                                    <div className="w-full h-full" style={{ background: cc.colore }} />
                                  </DraggableCell>
                                ))}
                              </div>
                            ) : (
                              <div className="w-full h-full flex flex-col gap-0.5 p-0.5">
                                {cellsList.slice(0, 3).map((cc, i) => (
                                  <DraggableCell
                                    key={i}
                                    id={`drag|${l.lavoratore_id}|${iso}|${cc.attivita_id || ""}|${cc.cantiere_id}`}
                                    disabled={!cc.attivita_id}
                                    className="flex-1 min-h-[10px] rounded-[2px] text-[9px] font-semibold text-white truncate flex items-center justify-between px-1"
                                    testid={`rp-drag-${l.lavoratore_id}-${iso}-${i}`}
                                    title={cellTitle([cc], null, iso)}
                                  >
                                    <span style={{ background: cc.colore }} className="w-full h-full rounded-[2px] flex items-center justify-between px-1">
                                      <span className="truncate">{cc.attivita_nome || cc.nome}</span>
                                      {cc.ore != null && <span className="opacity-80 ml-1">{cc.ore}h</span>}
                                    </span>
                                  </DraggableCell>
                                ))}
                              </div>
                            )}
                          </DroppableCell>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DndContext>
      )}
    </div>
  );
}

function parseCellId(id) {
  if (!id) return null;
  const s = String(id);
  if (s.startsWith("drag|")) {
    const [, lavId, giorno, attivitaId, cantiereId] = s.split("|");
    return { kind: "drag", lavId, giorno, attivitaId: attivitaId || null, cantiereId };
  }
  if (s.startsWith("cell|")) {
    const [, lavId, giorno] = s.split("|");
    return { kind: "cell", lavId, giorno };
  }
  return null;
}

function buildGrid(data) {
  const empty = { giorni: [], byLavGiorno: new Map(), ferieMap: new Map() };
  if (!data) return empty;
  const giorni = [];
  const s = new Date(data.range.inizio + "T00:00:00Z");
  for (let i = 0; i < data.range.giorni; i++) {
    const d = new Date(s.getTime() + i * 86400000);
    giorni.push(d.toISOString().slice(0, 10));
  }
  const cantById = new Map(data.cantieri.map((c) => [c.cantiere_id, c]));
  const byLavGiorno = new Map();
  for (const a of data.assegnazioni || []) {
    const c = cantById.get(a.cantiere_id);
    if (!c) continue;
    const k = `${a.lavoratore_id}|${a.giorno}`;
    const arr = byLavGiorno.get(k) || [];
    arr.push({
      cantiere_id: a.cantiere_id,
      nome: c.nome,
      colore: c.colore,
      attivita_id: a.attivita_id || null,
      attivita_nome: a.attivita_nome || "",
      attivita_stato: a.attivita_stato || "",
      ore: a.ore != null ? a.ore : null,
    });
    byLavGiorno.set(k, arr);
  }
  const ferieMap = new Map();
  for (const f of data.ferie || []) {
    ferieMap.set(`${f.lavoratore_id}|${f.giorno}`, f.tipo);
  }
  return { giorni, byLavGiorno, ferieMap };
}

function cellTitle(cells, ferie, iso) {
  if (ferie) return `${iso} · ${ferie}`;
  if (!cells || !cells.length) return iso;
  return `${iso}\n${cells
    .map((c) => `${c.nome}${c.attivita_nome ? " · " + c.attivita_nome : ""}${c.ore != null ? ` (${c.ore}h)` : ""}`)
    .join("\n")}`;
}
